import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Send, Paperclip, Loader2, StickyNote, Check, CheckCheck, Image as ImageIcon, FileText, X, Download, Smile, Sparkles, Languages, FileSearch, ThumbsUp, ThumbsDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const QUICK_EMOJIS = ["😊", "🙏", "👍", "🎉", "🏠", "✅", "❤️", "👋", "🔥", "💯", "📐", "🛠️", "✨", "🙌", "👏", "📞", "📧", "💰", "📝", "⚡", "😂", "🥳", "🤝", "📸"];

/**
 * WhatsApp-Web style message thread.
 *
 * Props:
 *  - conversationId: string (admin/designer mode; customer mode auto-resolves)
 *  - role: "customer" | "designer" | "admin"
 *  - apiBase: prefix for messages endpoints (e.g. "/inbox", "/designer/inbox/:id", "/admin/inbox/:id")
 *  - canInternalNote: bool (designer/admin only)
 *  - header: optional ReactNode rendered above the thread
 *  - onUnreadChange: optional callback when read state changes
 *  - liveMessage: object pushed via WebSocket - { conversation_id, message }
 *  - liveTyping: object pushed via WebSocket - { conversation_id, from, from_role, active }
 *  - sendTyping(conversationId, start): broadcast typing event
 */
export default function MessageThread({
  conversationId,
  role,
  apiBase,
  canInternalNote = false,
  header,
  onUnreadChange,
  liveMessage,
  liveTyping,
  sendTyping,
  draftInjection,            // {draft, style, ai_state_intent, ai_state_confidence, _key} — bumping _key inserts a fresh draft
  onMessageSent,             // (msg, aiContext) => void — fires after a successful send so parent can log AI reply provenance
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [internalMode, setInternalMode] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null); // {file_id, url, file_name, content_type, size, is_image}
  const [peerTyping, setPeerTyping] = useState(null); // {from, _t}
  const [showEmoji, setShowEmoji] = useState(false);
  const [showAi, setShowAi] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [activeDraftContext, setActiveDraftContext] = useState(null); // {draft, style, ai_state_intent, ai_state_confidence}

  const aiEnabled = role === "admin" || role === "designer" || role === "sales";
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimerRef = useRef(null);
  const localTypingRef = useRef(false);

  const insertEmoji = (e) => {
    const ta = textareaRef.current;
    if (!ta) { setText((t) => t + e); return; }
    const start = ta.selectionStart, end = ta.selectionEnd;
    setText((t) => t.slice(0, start) + e + t.slice(end));
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + e.length; }, 0);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${apiBase}/messages`);
      setMessages(data || []);
    } catch (e) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  const markRead = useCallback(async () => {
    try {
      await api.post(`${apiBase}/read`);
      onUnreadChange?.();
    } catch (e) { /* noop */ }
  }, [apiBase, onUnreadChange]);

  useEffect(() => {
    if (!conversationId && role !== "customer") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`${apiBase}/messages`);
        if (cancelled) return;
        setMessages(data || []);
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
      // Mark read (best-effort)
      api.post(`${apiBase}/read`).then(() => { if (!cancelled) onUnreadChange?.(); }).catch(() => {});
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, role, apiBase]);

  // Append live message arrivals OR apply message-updated patches
  useEffect(() => {
    if (!liveMessage) return;
    const { conversation_id, message, message_id, patch, _kind } = liveMessage;
    if (role !== "customer" && conversation_id !== conversationId) return;
    Promise.resolve().then(() => {
      setMessages(prev => {
        if (_kind === "patch") {
          // Merge wa_status / wa_error / status etc. into matching message in place
          return prev.map(m => (m.id === message_id ? { ...m, ...(patch || {}) } : m));
        }
        if (!message) return prev;
        if (prev.find(m => m.id === message.id)) return prev;
        return [...prev, message];
      });
      api.post(`${apiBase}/read`).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMessage]);

  // Live typing indicator from peer
  useEffect(() => {
    if (!liveTyping) return;
    if (role !== "customer" && liveTyping.conversation_id !== conversationId) return;
    const active = liveTyping.active;
    const next = active ? { from: liveTyping.from || "Typing", _t: liveTyping._t || Date.now() } : null;
    Promise.resolve().then(() => setPeerTyping(next));
  }, [liveTyping, conversationId, role]);

  // Auto-clear stale typing indicator after 4s of no update
  useEffect(() => {
    if (!peerTyping) return;
    const t = setTimeout(() => setPeerTyping(null), 4000);
    return () => clearTimeout(t);
  }, [peerTyping]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, peerTyping]);

  // Phase U.1: When the parent injects an AI draft, insert it into the composer
  // and remember its context so we can log provenance on send.
  useEffect(() => {
    if (!draftInjection || !draftInjection.draft) return;
    setText(draftInjection.draft);
    setActiveDraftContext(draftInjection);
    textareaRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftInjection?._key]);

  // Broadcast typing.start as the user types, typing.stop after 1.5s silence
  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (!sendTyping || !conversationId) return;
    if (v && !localTypingRef.current) {
      sendTyping(conversationId, true);
      localTypingRef.current = true;
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (localTypingRef.current) {
        sendTyping(conversationId, false);
        localTypingRef.current = false;
      }
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (localTypingRef.current && sendTyping && conversationId) {
        sendTyping(conversationId, false);
        localTypingRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  const onFileChoose = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    if (f.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const isImage = f.type.startsWith("image/");
      // Admin/designer can drop into media library; customer uses /upload/reference
      if (role === "admin" || role === "designer") {
        fd.append("category", isImage ? "images" : "other");
        const r = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const it = r.data;
        setPendingAttachment({
          file_id: it.id, url: it.public_url, file_name: it.file_name,
          content_type: it.content_type, size: it.size, is_image: (it.content_type || "").startsWith("image/"),
        });
      } else {
        const r = await api.post("/upload/reference", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPendingAttachment({
          file_id: r.data.id, url: `/api/files/${r.data.id}`, file_name: f.name,
          content_type: f.type || "application/octet-stream", size: f.size, is_image: isImage,
        });
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setAttaching(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body && !pendingAttachment) return;
    setSending(true);
    const att = pendingAttachment ? [{
      file_id: pendingAttachment.file_id,
      url: pendingAttachment.url,
      file_name: pendingAttachment.file_name,
      content_type: pendingAttachment.content_type,
      size: pendingAttachment.size,
    }] : null;
    const msgType = pendingAttachment ? (pendingAttachment.is_image ? "image" : "document") : "text";
    const payload = { body: body || "", type: msgType };
    if (att) payload.attachments = att;
    if (internalMode && canInternalNote) payload.internal_note = true;
    try {
      // Best-effort stop-typing
      if (sendTyping && conversationId && localTypingRef.current) {
        sendTyping(conversationId, false);
        localTypingRef.current = false;
      }
      await api.post(`${apiBase}/messages`, payload);
      const sentBody = body;
      const ctx = activeDraftContext;
      setText("");
      setPendingAttachment(null);
      setActiveDraftContext(null);
      setTimeout(load, 400);
      // Notify parent so it can log the AI reply provenance (Phase U.1 Hybrid-Mode audit trail)
      if (onMessageSent && !payload.internal_note) {
        try { onMessageSent({ final_text: sentBody, draft_context: ctx }); } catch { /* ignore */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Group messages by date for date-separators (WhatsApp style)
  const grouped = useMemo(() => {
    const groups = [];
    let lastDate = null;
    for (const m of messages) {
      const d = new Date(m.created_at);
      const key = d.toDateString();
      if (key !== lastDate) {
        groups.push({ type: "date", key, date: d });
        lastDate = key;
      }
      groups.push({ type: "msg", message: m });
    }
    return groups;
  }, [messages]);

  return (
    <div className="flex flex-col h-full bg-[#efeae2] rounded-2xl overflow-hidden" data-testid={`thread-${role}`}>
      {header}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 space-y-1.5 relative"
        style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'><g fill='%23d9d2c5' fill-opacity='0.18'><path d='M30 5 L33 25 L53 28 L33 31 L30 51 L27 31 L7 28 L27 25 Z'/></g></svg>\")",
          backgroundRepeat: "repeat",
        }}
        data-testid="thread-messages"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-12 bg-white/60 mx-auto max-w-md rounded-xl px-4 py-3">
            {role === "customer" ? "Start a conversation with HouseDesignWala. We're here to help!" : "No messages yet."}
          </div>
        ) : (
          grouped.map((g, idx) => g.type === "date"
            ? <DateSeparator key={`d-${idx}`} date={g.date} />
            : <MessageBubble key={g.message.id} m={g.message} role={role} conversationId={conversationId} />
          )
        )}
        {peerTyping && (
          <div className="flex justify-start pt-1" data-testid="thread-typing">
            <div className="px-3 py-2 rounded-2xl bg-white shadow-sm border border-black/5 flex items-center gap-2">
              <span className="text-[11px] font-semibold text-[#075e54]">{peerTyping.from}</span>
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-black/10 bg-[#f0f2f5]">
        {canInternalNote && (
          <label className="flex items-center gap-1.5 text-[11px] text-gray-700 px-3 pt-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={internalMode}
              onChange={(e) => setInternalMode(e.target.checked)}
              data-testid="internal-note-toggle"
              className="w-3.5 h-3.5 accent-[#F7941D]"
            />
            <StickyNote className="w-3.5 h-3.5" />
            Internal note <span className="text-gray-400">(hidden from customer)</span>
          </label>
        )}
        {pendingAttachment && (
          <div className="px-3 pt-2 flex items-center gap-2" data-testid="pending-attachment">
            <div className="flex-1 flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 border border-gray-200">
              {pendingAttachment.is_image
                ? <ImageIcon className="w-4 h-4 text-emerald-600" />
                : <FileText className="w-4 h-4 text-[#1a3c5e]" />}
              <span className="text-xs font-semibold text-[#1a3c5e] truncate flex-1">{pendingAttachment.file_name}</span>
              <span className="text-[10px] text-gray-400">{humanSize(pendingAttachment.size)}</span>
              <button onClick={() => setPendingAttachment(null)} data-testid="cancel-attachment" className="text-gray-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}
        {showAi && aiEnabled && (
          <div className="px-3 pt-2" data-testid="ai-panel">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-2.5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-purple-700"><Sparkles className="w-3.5 h-3.5" /> AI assistant <span className="text-[10px] font-normal text-gray-500">· Gemini 3 Flash</span></div>
                <button onClick={() => setShowAi(false)} className="text-gray-400 hover:text-gray-700" data-testid="ai-panel-close"><X className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={async () => {
                  setAiBusy(true); setAiSuggestions([]); setAiSummary("");
                  try {
                    const r = await api.post("/admin/ai/smart-reply", { conversation_id: conversationId, style: "hinglish", count: 3 });
                    setAiSuggestions(r.data.suggestions || []);
                  } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
                  finally { setAiBusy(false); }
                }} disabled={aiBusy} data-testid="ai-smart-reply-btn"
                  className="px-2.5 py-1 bg-white border border-purple-300 rounded-full text-[11px] font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-50 inline-flex items-center gap-1">
                  {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Smart reply (Hinglish)
                </button>
                <button onClick={async () => {
                  setAiBusy(true); setAiSuggestions([]); setAiSummary("");
                  try {
                    const r = await api.post("/admin/ai/smart-reply", { conversation_id: conversationId, style: "english", count: 3 });
                    setAiSuggestions(r.data.suggestions || []);
                  } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
                  finally { setAiBusy(false); }
                }} disabled={aiBusy} data-testid="ai-smart-reply-en-btn"
                  className="px-2.5 py-1 bg-white border border-purple-300 rounded-full text-[11px] font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-50">
                  English replies
                </button>
                <button onClick={async () => {
                  setAiBusy(true); setAiSuggestions([]);
                  try {
                    const r = await api.post("/admin/ai/summarize", { conversation_id: conversationId, style: "short" });
                    setAiSummary(r.data.summary || "");
                  } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
                  finally { setAiBusy(false); }
                }} disabled={aiBusy} data-testid="ai-summarize-btn"
                  className="px-2.5 py-1 bg-white border border-blue-300 rounded-full text-[11px] font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center gap-1">
                  <FileSearch className="w-3 h-3" /> Summarize chat
                </button>
                <button onClick={async () => {
                  if (!text.trim()) { toast.error("Type something to translate first"); return; }
                  setAiBusy(true);
                  try {
                    const r = await api.post("/admin/ai/translate", { text, target: "english" });
                    setText(r.data.translated || text);
                  } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
                  finally { setAiBusy(false); }
                }} disabled={aiBusy || !text.trim()} data-testid="ai-translate-en-btn"
                  className="px-2.5 py-1 bg-white border border-emerald-300 rounded-full text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 inline-flex items-center gap-1">
                  <Languages className="w-3 h-3" /> Draft → English
                </button>
                <button onClick={async () => {
                  if (!text.trim()) { toast.error("Type something to translate first"); return; }
                  setAiBusy(true);
                  try {
                    const r = await api.post("/admin/ai/translate", { text, target: "hinglish" });
                    setText(r.data.translated || text);
                  } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
                  finally { setAiBusy(false); }
                }} disabled={aiBusy || !text.trim()} data-testid="ai-translate-hg-btn"
                  className="px-2.5 py-1 bg-white border border-emerald-300 rounded-full text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                  Draft → Hinglish
                </button>
              </div>
              {aiSuggestions.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="ai-suggestions">
                  {aiSuggestions.map((s, i) => (
                    <button key={i} onClick={() => { setText(s); setShowAi(false); textareaRef.current?.focus(); }}
                      data-testid={`ai-suggestion-${i}`}
                      className="w-full text-left bg-white border border-purple-200 hover:bg-purple-50 rounded-lg px-3 py-2 text-xs text-gray-800">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {aiSummary && (
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-gray-800 italic" data-testid="ai-summary">
                  {aiSummary}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="p-2 flex gap-1.5 items-end relative">
          <input ref={fileInputRef} type="file" className="hidden" onChange={onFileChoose} data-testid="attach-file-input" accept="image/*,application/pdf,.doc,.docx,.dwg,.dxf,.zip" />
          {aiEnabled && (
            <button
              onClick={() => setShowAi((v) => !v)}
              data-testid="ai-toggle-btn"
              className={`p-2 rounded-full hover:bg-black/5 ${showAi ? "text-purple-700 bg-purple-100" : "text-[#54656f]"}`}
              title="AI assistant"
              type="button"
            ><Sparkles className="w-5 h-5" /></button>
          )}
          <button
            onClick={() => setShowEmoji((v) => !v)}
            data-testid="emoji-btn"
            className="p-2 rounded-full hover:bg-black/5 text-[#54656f]"
            title="Emoji"
            type="button"
          ><Smile className="w-5 h-5" /></button>
          {showEmoji && (
            <div className="absolute bottom-14 left-2 z-20 bg-white border border-gray-200 rounded-2xl shadow-lg p-2 grid grid-cols-8 gap-1 w-72" data-testid="emoji-picker">
              {QUICK_EMOJIS.map((e) => (
                <button key={e} onClick={() => { insertEmoji(e); }} className="text-xl hover:bg-gray-100 rounded p-1" type="button" data-testid={`emoji-${e}`}>{e}</button>
              ))}
            </div>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={attaching || sending}
            data-testid="attach-btn"
            className="p-2 rounded-full hover:bg-black/5 text-[#54656f] disabled:opacity-50"
            title="Attach file"
            type="button"
          >{attaching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}</button>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={onTextChange}
            onKeyDown={onKey}
            onFocus={() => setShowEmoji(false)}
            rows={1}
            placeholder={internalMode ? "Private note for the team..." : "Type a message"}
            data-testid="thread-input"
            className={`flex-1 rounded-2xl px-3 py-2 text-sm focus:outline-none max-h-32 resize-none ${internalMode ? "bg-amber-50 border border-amber-300 focus:ring-2 focus:ring-amber-300" : "bg-white border border-transparent focus:border-gray-300"}`}
          />
          <button
            onClick={send}
            disabled={sending || (!text.trim() && !pendingAttachment)}
            data-testid="thread-send"
            className={`p-2.5 rounded-full text-white disabled:opacity-40 ${internalMode ? "bg-amber-500 hover:bg-amber-600" : "bg-[#00a884] hover:bg-[#008f72]"}`}
            title="Send"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function humanSize(b) {
  if (!b) return "";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + " KB";
  return (b / 1024 / 1024).toFixed(1) + " MB";
}

function DateSeparator({ date }) {
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = new Date(today.getTime() - 86400000).toDateString() === date.toDateString();
  const label = isToday
    ? "Today"
    : isYesterday ? "Yesterday"
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: today.getFullYear() === date.getFullYear() ? undefined : "numeric" });
  return (
    <div className="flex justify-center my-3">
      <div className="px-2.5 py-0.5 rounded-md bg-white/85 text-[11px] text-gray-600 font-medium shadow-sm">{label}</div>
    </div>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-0.5">
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

function MessageBubble({ m, role, conversationId }) {
  const isSelf = m.from_role === "self";
  const isSystem = m.type === "system_message" || m.from_role === "system";
  const isInternal = m.internal_note;
  const isImage = m.type === "image" || (m.attachments?.[0]?.content_type || "").startsWith("image/");
  const att = m.attachments?.[0];
  const isAudio = m.type === "audio" || (att?.content_type || "").startsWith("audio/");
  const aiActionsEnabled = role === "admin" && !isSelf && conversationId && att?.url;

  if (isSystem) {
    return (
      <div className="flex justify-center my-2" data-testid={`msg-system-${m.id}`}>
        <div className="px-3 py-1 rounded-md bg-white/85 text-gray-600 text-[11px] shadow-sm">{m.body}</div>
      </div>
    );
  }

  const bubbleColor = isInternal
    ? "bg-amber-100 border border-amber-300 text-amber-900"
    : isSelf
      ? "bg-[#d9fdd3] text-[#111b21]"   // WhatsApp green (outgoing)
      : "bg-white text-[#111b21] shadow-sm border border-black/5";

  return (
    <div className={`flex ${isSelf ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[78%] sm:max-w-[65%] rounded-lg ${isImage && att ? "p-1" : "px-2.5 py-1.5"} text-sm ${bubbleColor} relative`}
           style={{ borderTopLeftRadius: isSelf ? 8 : 2, borderTopRightRadius: isSelf ? 2 : 8 }}>
        {!isSelf && (
          <div className={`text-[11px] font-bold mb-0.5 ${isInternal ? "text-amber-700" : "text-[#06cf9c]"} ${isImage && att ? "px-2 pt-1" : ""}`}>
            {m.from}{isInternal && " · INTERNAL NOTE"}
          </div>
        )}
        {/* Image attachment preview */}
        {isImage && att && (
          <a href={att.url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={att.url}
              alt={att.file_name || "image"}
              loading="lazy"
              className="rounded-md max-w-full max-h-72 object-cover cursor-zoom-in"
              onError={(e) => {
                // Fallback: hide broken img and reveal sibling document link
                e.currentTarget.style.display = "none";
                const next = e.currentTarget.parentElement?.nextElementSibling;
                if (next) next.style.display = "flex";
              }}
            />
          </a>
        )}
        {/* Fallback document link for images that fail to load (hidden by default) */}
        {isImage && att && (
          <a
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            download={att.file_name}
            style={{ display: "none" }}
            className="items-center gap-2 p-2 mt-1 bg-black/5 rounded-md text-xs text-gray-700 hover:bg-black/10"
          >
            <ImageIcon className="w-4 h-4 text-[#06cf9c]" /> {att.file_name || "image"} <Download className="w-3 h-3 ml-auto" />
          </a>
        )}
        {/* Document attachment */}
        {!isImage && !isAudio && att && (
          <a
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            download={att.file_name}
            className="flex items-center gap-2 bg-black/5 rounded-md px-2 py-2 my-1 hover:bg-black/10"
            data-testid={`msg-doc-${m.id}`}
          >
            <FileText className="w-5 h-5 text-[#1a3c5e]" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#1a3c5e] truncate">{att.file_name || "Document"}</div>
              <div className="text-[10px] text-gray-500">{humanSize(att.size)}</div>
            </div>
            <Download className="w-4 h-4 text-gray-500" />
          </a>
        )}
        {/* Audio attachment */}
        {isAudio && att && (
          <div className="my-1" data-testid={`msg-audio-${m.id}`}>
            <audio controls src={att.url} className="max-w-full" preload="metadata" />
          </div>
        )}
        {/* AI multimodal actions */}
        {aiActionsEnabled && (isAudio || isImage || (!isImage && !isAudio && att)) && (
          <AIMultimodalActions
            m={m}
            att={att}
            conversationId={conversationId}
            kind={isAudio ? "audio" : (isImage ? "image" : "document")}
          />
        )}
        {/* Click-to-WhatsApp ad context — the ad creative the customer clicked, separate
            from whatever they typed. Staff-only (inbox.py::scrub_message_for_role never
            sends this field to role="customer"). */}
        {m.ad_referral && (m.ad_referral.headline || m.ad_referral.body) && (
          <div className="mb-1 rounded-md bg-[#eef7f4] border border-[#06cf9c]/30 px-2 py-1.5 text-xs" data-testid={`msg-ad-referral-${m.id}`}>
            <div className="text-[9px] uppercase font-bold text-[#06cf9c] mb-0.5">Clicked ad</div>
            {m.ad_referral.image_url && (
              <img src={m.ad_referral.image_url} alt="" loading="lazy" className="rounded max-h-32 mb-1 object-cover" />
            )}
            {m.ad_referral.headline && <div className="font-semibold text-gray-800">{m.ad_referral.headline}</div>}
            {m.ad_referral.body && <div className="text-gray-600">{m.ad_referral.body}</div>}
          </div>
        )}
        {/* Body text */}
        {m.body && (
          <div className={`whitespace-pre-wrap break-words ${isImage && att ? "px-1.5 pb-1" : ""}`}>{m.body}</div>
        )}
        {/* A8: CSAT proxy — thumbs on AI-authored replies, admin view only */}
        {role === "admin" && m.ai_initiated && (
          <AIFeedback m={m} conversationId={conversationId} />
        )}
        {/* Metadata footer */}
        <div className={`text-[10px] mt-0.5 flex items-center justify-end gap-0.5 ${isInternal ? "text-amber-700" : "text-gray-500"} ${isImage && att ? "px-1.5 pb-1" : ""}`}>
          {/* WhatsApp delivery status badge (only on outgoing customer-facing msgs in WA convs) */}
          {isSelf && !isInternal && m.wa_status && m.wa_status !== "sent" && (
            <span
              className={`mr-1 px-1.5 py-px rounded text-[9px] font-bold uppercase ${
                m.wa_status === "disabled" ? "bg-rose-100 text-rose-700"
                : m.wa_status === "window_closed" ? "bg-amber-100 text-amber-700"
                : m.wa_status === "no_phone" ? "bg-gray-200 text-gray-700"
                : m.wa_status === "failed" ? "bg-rose-100 text-rose-700"
                : "bg-gray-100 text-gray-700"
              }`}
              title={m.wa_error || m.wa_status}
              data-testid={`wa-status-${m.id}`}
            >
              {m.wa_status === "disabled" ? "Not delivered · setup needed"
                : m.wa_status === "window_closed" ? "24h window closed · use template"
                : m.wa_status === "no_phone" ? "No phone"
                : m.wa_status === "failed" ? "Failed"
                : m.wa_status}
            </span>
          )}
          <span>{new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
          {isSelf && !isInternal && (
            m.wa_status === "sent" || m.status === "delivered" || m.status === "read"
              ? (m.status === "read"
                  ? <CheckCheck className="w-3.5 h-3.5 text-[#53bdeb]" />
                  : <CheckCheck className="w-3.5 h-3.5 text-gray-500" />)
              : <Check className="w-3.5 h-3.5 text-gray-500" />
          )}
        </div>
      </div>
    </div>
  );
}


function AIFeedback({ m, conversationId }) {
  const [feedback, setFeedback] = useState(m.ai_feedback || null);
  const [busy, setBusy] = useState(false);

  const rate = async (rating) => {
    if (busy || feedback) return;
    setBusy(true);
    try {
      await api.post("/admin/ai/copilot/reply-feedback", { message_id: m.id, conversation_id: conversationId, rating });
      setFeedback(rating);
    } catch {
      toast.error("Couldn't save feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 mt-1" data-testid={`ai-feedback-${m.id}`}>
      <span className="text-[9px] uppercase font-bold tracking-wide text-purple-500">AI reply</span>
      <button onClick={() => rate("up")} disabled={busy || !!feedback} data-testid={`ai-feedback-up-${m.id}`}
        title="Good reply" className={`p-0.5 rounded ${feedback === "up" ? "text-emerald-600" : feedback ? "text-gray-300" : "text-gray-400 hover:text-emerald-600"}`}>
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => rate("down")} disabled={busy || !!feedback} data-testid={`ai-feedback-down-${m.id}`}
        title="Poor reply" className={`p-0.5 rounded ${feedback === "down" ? "text-rose-600" : feedback ? "text-gray-300" : "text-gray-400 hover:text-rose-600"}`}>
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function AIMultimodalActions({ m, att, conversationId, kind }) {
  const [busy, setBusy] = useState(null); // 'transcribe' | 'analyze' | 'extract'
  const [result, setResult] = useState(null);

  const run = async (action) => {
    setBusy(action);
    try {
      const fd = new FormData();
      fd.append("conversation_id", conversationId);
      fd.append("message_id", m.id);
      fd.append("file_url", att.url);
      let path = "";
      if (action === "transcribe") path = "/admin/ai/copilot/transcribe-voice";
      else if (action === "analyze") path = "/admin/ai/copilot/analyze-image";
      else if (action === "extract") { path = "/admin/ai/copilot/extract-document"; fd.append("apply", "true"); }
      const r = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ action, data: r.data });
      if (action === "transcribe") toast.success("Transcribed");
      else if (action === "analyze") toast.success("Image analysed");
      else if (action === "extract") toast.success(r.data.applied ? "Extracted & applied to lead" : "Extracted");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("AI budget exceeded");
      else if (e?.response?.status === 415) toast.error("Unsupported format (DWG cannot be read by AI)");
      else toast.error(e?.response?.data?.detail || "AI failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`ai-mm-actions-${m.id}`}>
      {kind === "audio" && (
        <button onClick={() => run("transcribe")} disabled={!!busy} data-testid={`ai-mm-transcribe-${m.id}`}
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 inline-flex items-center gap-1 disabled:opacity-50">
          {busy === "transcribe" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />} Transcribe
        </button>
      )}
      {kind === "image" && (
        <button onClick={() => run("analyze")} disabled={!!busy} data-testid={`ai-mm-analyze-${m.id}`}
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 inline-flex items-center gap-1 disabled:opacity-50">
          {busy === "analyze" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />} Analyze with AI
        </button>
      )}
      {kind === "document" && (
        <button onClick={() => run("extract")} disabled={!!busy} data-testid={`ai-mm-extract-${m.id}`}
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 inline-flex items-center gap-1 disabled:opacity-50">
          {busy === "extract" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <FileSearch className="w-2.5 h-2.5" />} Extract → CRM
        </button>
      )}
      {result?.action === "transcribe" && result?.data?.transcript && (
        <div className="w-full mt-1 px-2 py-1.5 rounded-md bg-purple-50 border border-purple-200 text-[11px] text-gray-800" data-testid={`ai-mm-transcript-${m.id}`}>
          <div className="text-[9px] uppercase font-bold text-purple-700 mb-0.5">Transcript {result.data.intent && <span className="text-emerald-700 ml-1">· {result.data.intent}</span>}</div>
          {result.data.transcript}
        </div>
      )}
      {result?.action === "analyze" && (
        <div className="w-full mt-1 px-2 py-1.5 rounded-md bg-purple-50 border border-purple-200 text-[11px] text-gray-800" data-testid={`ai-mm-vision-${m.id}`}>
          <div className="text-[9px] uppercase font-bold text-purple-700 mb-0.5">
            Vision {result.data.service_detected && <span className="text-emerald-700 ml-1">· {result.data.service_detected}</span>}
          </div>
          <div>{result.data.summary}</div>
          {result.data.recommended_package && <div className="text-[10px] text-gray-600 mt-0.5"><strong>Pkg:</strong> {result.data.recommended_package}</div>}
          {result.data.suggested_reply && <div className="text-[10px] text-gray-600 mt-0.5 italic">&ldquo;{result.data.suggested_reply}&rdquo;</div>}
        </div>
      )}
      {result?.action === "extract" && (
        <div className="w-full mt-1 px-2 py-1.5 rounded-md bg-purple-50 border border-purple-200 text-[11px] text-gray-800" data-testid={`ai-mm-doc-${m.id}`}>
          <div className="text-[9px] uppercase font-bold text-purple-700 mb-0.5">
            Extracted {result.data.applied && <span className="text-emerald-700 ml-1">· applied to lead</span>}
          </div>
          <ul className="space-y-0.5">
            {Object.entries(result.data.extracted || {}).filter(([, v]) => v !== null && v !== "").map(([k, v]) => (
              <li key={k}><strong>{k.replace(/_/g, " ")}:</strong> {String(v)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
